import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { Formik, Form, Field, FieldArray, getIn } from "formik";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faAngleRight,
  faAngleLeft,
  faEllipsis,
  faSort,
  faSortUp,
  faSortDown,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import * as Yup from "yup";
import { debounce } from "lodash";
import { getCustomerForUI } from "../../store/reducer/customers/customersSlice";
import { getDdOptionForUI } from "../../store/reducer/ddOption/ddOptionSlice";
import { getUserForUI } from "../../store/reducer/user/userSlice";
import {
  listDrawings,
  searchDrawings,
  updateBulkDrawings,
} from "../../store/reducer/drawing/drawingSlice";
import moment from "moment";

function ListDrawings() {
  const dispatch = useDispatch();
  const [search, setSearch] = useState("");
  const formRef = useRef(null);
  const {
    loading,
    drawings = [],
    totalCount,
  } = useSelector((state) => state.drawing);
  const { options = [] } = useSelector((state) => state.ddOptions);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isDeleted, setIsDeleted] = useState(false);

  const [sortBy, setSortBy] = useState(" expires_at");
  const [sortOrder, setSortOrder] = useState("desc");
  const [editingDrawings, setEditingDrawings] = useState({});
  const [selectedDrawings, setSelectedDrawings] = useState(new Set());
  const [customerData, setCustomerData] = useState([]);
  const [userData, setUserData] = useState([]);

  const [filtersActive, setFiltersActive] = useState(false);
  const [filterParams, setFilterParams] = useState({
    customer_id: "",
    drawing_status: [],
    date_required: "",
    requested_by: "",
    designer_id: "",
  });

  // Color map from AddDdOption
  const colorMap = {
    slate: "bg-slate-500",
    gray: "bg-gray-500",
    zinc: "bg-zinc-500",
    neutral: "bg-neutral-500",
    stone: "bg-stone-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
    amber: "bg-amber-500",
    yellow: "bg-yellow-400",
    lime: "bg-lime-500",
    green: "bg-green-500",
    emerald: "bg-emerald-500",
    teal: "bg-teal-500",
    cyan: "bg-cyan-500",
    sky: "bg-sky-500",
    blue: "bg-blue-500",
    indigo: "bg-indigo-500",
    violet: "bg-violet-500",
    purple: "bg-purple-500",
    fuchsia: "bg-fuchsia-500",
    pink: "bg-pink-500",
    rose: "bg-rose-500",
  };

  const ErrorMessage = ({ name }) => (
    <Field
      name={name}
      render={({ form }) => {
        const error = getIn(form.errors, name);
        const touch = getIn(form.touched, name);
        return touch && error ? (
          <div className="text-red-700">{error}</div>
        ) : null;
      }}
    />
  );

  useEffect(() => {
    dispatch(getCustomerForUI()).then((result) => {
      if (result.payload) {
        setCustomerData(result.payload);
      }
    });
    dispatch(getUserForUI()).then((result) => {
      if (result.payload) {
        setUserData(result.payload);
      }
    });
    dispatch(getDdOptionForUI());
    dispatch(listDrawings({ search, page, limit, sortBy, sortOrder }));
  }, []);

  const handleSearch = (val = "") => {
    setPage(1);
    if (filtersActive) {
      dispatch(searchDrawings({ ...filterParams, page: 1, limit, sortBy, sortOrder }));
    } else {
      dispatch(listDrawings({ search: val, page: 1, limit, sortBy, sortOrder }));
    }
  };
  const debouncedSearch = useCallback(debounce(handleSearch, 800), [
    filtersActive,
    filterParams,
    sortBy,
    sortOrder,
    limit,
  ]);

  const handlePageClick = ({ selected }) => {
    const newPage = selected + 1;
    setPage(newPage);
    if (filtersActive) {
      dispatch(searchDrawings({ ...filterParams, page: newPage, limit, sortBy, sortOrder }));
    } else {
      dispatch(listDrawings({ search, page: newPage, limit, sortBy, sortOrder }));
    }
  };

  const handleResetFilterForm = () => {
    if (formRef.current) {
      formRef.current.resetForm();
    }
    setFiltersActive(false);
    setFilterParams({
      customer_id: "",
      drawing_status: [],
      date_required: "",
      requested_by: "",
      designer_id: "",
    });
    setPage(1);
    dispatch(listDrawings({ search, page: 1, limit, sortBy, sortOrder }));
  };

  const toggleSort = (key) => {
    const direction = sortBy === key && sortOrder === "asc" ? "desc" : "asc";
    setSortBy(key);
    setSortOrder(direction);
    setPage(1);
    if (filtersActive) {
      dispatch(searchDrawings({ ...filterParams, page: 1, limit, sortBy: key, sortOrder: direction }));
    } else {
      dispatch(listDrawings({ search, page: 1, limit, sortBy: key, sortOrder: direction }));
    }
  };

  const toggleSelection = (id) => {
    const newSet = new Set(selectedDrawings);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedDrawings(newSet);
  };

  const updateField = (id, field, value) => {
    setEditingDrawings((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value } }));
  };

  const handleBulkEditSubmit = async (values, { setSubmitting, resetForm }) => {
    setSubmitting(true);
    values.updatedBy = JSON.parse(localStorage.getItem("userSession")).id;
    const formData = new FormData();
    Object.keys(values).forEach((fieldName) => {
      if (fieldName === "drawings") {
        formData.append(fieldName, JSON.stringify(values[fieldName]));
      } else {
        formData.append(fieldName, values[fieldName]);
      }
    });

    await dispatch(updateBulkDrawings(formData)).then((result) => {
      if (result.payload) {
        toast.success(result.payload.message);
        dispatch(listDrawings({ search, page, limit, sortBy, sortOrder }));
      } else {
        toast.error(result.error.message);
      }
    });
    setSubmitting(false);
  };

  const validationSchema = Yup.object().shape({
    drawings: Yup.array().of(
      Yup.object().shape({
        // Uncomment if validation is needed
        // priority: Yup.number()
        //   .integer("Priority must be an integer")
        //   .min(1, "Priority must be greater than 0")
        //   .required("Required"),
        // designer_id: Yup.number()
        //   .integer("Designer must be an integer")
        //   .min(1, "Designer must be greater than 0")
        //   .required("Required"),
        // status: Yup.number()
        //   .integer("Status must be an integer")
        //   .min(1, "Status must be greater than 0")
        //   .required("Required"),
      })
    ).required("Must have drawings"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    setFiltersActive(true);
    setFilterParams(values);
    setPage(1);
    await dispatch(
      searchDrawings({
        ...values,
        page: 1,
        limit,
        sortBy,
        sortOrder,
      })
    );
    setSubmitting(false);
  };

  const priorityOptions = options
    .filter((option) => option.ui_name === "Drawing" && option.field_name === "Priority")
    .sort((a, b) => a.sort_field - b.sort_field);

  const statusOptions = options
    .filter((option) => option.ui_name === "Drawing" && option.field_name === "Status")
    .sort((a, b) => a.sort_field - b.sort_field);

  // Function to get Tailwind class for a given option ID
  const getFieldColorClass = (optionId, fieldType) => {
    const option = options.find(
      (opt) => opt.ui_name === "Drawing" && opt.field_name === fieldType && String(opt.id) === String(optionId)
    );
    return option && option.field_color ? colorMap[option.field_color] || "" : "";
  };

  return (
    <div className="flex flex-col mt-[20px] gap-[40px]">
      <div className="bg-grey md:flex gap-[10px]">
        <div className="w-full md:w-[28%] md:flex flex-col justify-between">
          <div className="h-[114px] bg-[#5E6FF0] rounded-[12px] border-[10px] border-[#5E6FF0] p-[20px] flex items-center justify-between mb-3">
            <div className="flex flex-col gap-[10px]">
              <span className="text-[32px] leading-[40px] text-white font-Mulish font-[700]">
                {totalCount}
              </span>
              <span className="text-[16px] text-white font-Mulish font-[700]">
                Records returned
              </span>
            </div>
            <div>
              <img
                src={process.env.PUBLIC_URL + "/icons/pad.png"}
                className="w-[88px] h-[88px]"
                alt=""
              />
            </div>
          </div>
        </div>
        <div className="w-full md:w-[72%] md:flex gap-[24px] flex-col bg-[#F8F8F8] rounded-[12px] p-6">
          <div className="flex flex-row justify-between pb-[20px] border-b border-[#E1E1E1] border-solid">
            <div>
              <span className="font-Inter font-[600] text-[18px] leading-[21px] text-[#1C1C20]">
                Search Your Drawing
              </span>
            </div>
            <div>
              <span className="cursor-pointer" onClick={handleResetFilterForm}>
                <img
                  src={process.env.PUBLIC_URL + "/icons/reset.png"}
                  alt=""
                  className="w-[24px] cursor-pointer"
                />
              </span>
            </div>
          </div>
          <Formik
            innerRef={formRef}
            initialValues={{
              customer_id: "",
              drawing_status: [],
              date_required: "",
              requested_by: "",
              designer_id: "",
            }}
            onSubmit={handleSubmit}
          >
            {({
              isSubmitting,
              values,
              errors,
              touched,
              setFieldValue,
              resetForm,
              handleSubmit,
            }) => (
              <Form>
                <div className="md:grid md:grid-cols-5 gap-3">
                  <div className="mb-2">
                    <label
                      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                      htmlFor="customer_id"
                    >
                      Customer
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        id="customer_id"
                        name="customer_id"
                        onChange={(e) => {
                          setFieldValue("customer_id", e.target.value);
                        }}
                      >
                        <option value={""}>Select Customer</option>
                        {customerData.map((customer) => (
                          <option value={customer.id} key={customer.id}>
                            {customer.customer_name}
                          </option>
                        ))}
                      </Field>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg
                          className="fill-current h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label
                      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                      htmlFor="requested_by"
                    >
                      Sales Representative
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        id="requested_by"
                        name="requested_by"
                        onChange={(e) => {
                          setFieldValue("requested_by", e.target.value);
                        }}
                      >
                        <option value={""}>Select Sales Representative</option>
                        {userData.map((user) => (
                          <option value={user.id} key={user.id}>
                            {user.firstname} {user.lastname}
                          </option>
                        ))}
                      </Field>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg
                          className="fill-current h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label
                      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                      htmlFor="designer_id"
                    >
                      Designer
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        id="designer_id"
                        name="designer_id"
                        onChange={(e) => {
                          setFieldValue("designer_id", e.target.value);
                        }}
                      >
                        <option value={""}>Select Designer</option>
                        {userData.map((user) => (
                          <option value={user.id} key={user.id}>
                            {user.firstname} {user.lastname}
                          </option>
                        ))}
                      </Field>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg
                          className="fill-current h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label
                      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                      htmlFor="drawing_status"
                    >
                      Drawing Status
                    </label>
                    <div className="relative">
                      <Field
                        as="select"
                        multiple
                        className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                        id="drawing_status"
                        name="drawing_status"
                        onChange={(e) => {
                          const selectedValues = Array.from(
                            e.target.selectedOptions,
                            (option) => option.value
                          );
                          setFieldValue("drawing_status", selectedValues);
                        }}
                      >
                        <option value={""}>Select Status</option>
                        {statusOptions.map((option) => (
                          <option key={option.id} value={option.id} selected={option.is_default}>
                            {option.field_code}
                          </option>
                        ))}
                      </Field>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                        <svg
                          className="fill-current h-4 w-4"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="mb-2">
                    <label
                      className="font-Inter font-[400] text-[15px] text-[#9E9E9E] leading-[17px]"
                      htmlFor="date_required"
                    >
                      Date Required
                    </label>
                    <Field
                      className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pr-8 rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                      type="date"
                      id="date_required"
                      name="date_required"
                      onChange={(e) => {
                        setFieldValue("date_required", e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="flex bg-[#F8F8F8] rounded-[12px] mt-2">
                  <div>
                    <button
                      type="submit"
                      name="submit"
                      className={`py-[10px] px-[70px] text-center text-[#55A14A] border border-[#55A14A] w-full rounded-[12px] text-[16px] ${
                        isSubmitting ? "bg-gray-300" : "bg-white"
                      }`}
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <FontAwesomeIcon icon={faSpinner} spin />
                      ) : (
                        "Search"
                      )}
                    </button>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
      <div className="">
        <div className="flex justify-between items-center">
          <div className="leading-[30px] font-Mulish font-[700] text-[24px]">
            <span className="">List Of Drawings</span>
          </div>
        </div>
        <div className="md:p-5 bg-[#F8F8F8] mt-[20px] rounded-l-[12px]">
          <div className="flex flex-col">
            <div className="md:flex justify-end items-center p-[12px]">
              <div className="relative">
                <input
                  className="block appearance-none w-full bg-white border border-gray-200 text-gray-700 py-3 px-4 pl-[60px] rounded-[12px] leading-tight focus:outline-none focus:bg-white focus:border-gray-500"
                  type="text"
                  id="search"
                  name="search"
                  onChange={(e) => {
                    setSearch(e.target.value);
                    debouncedSearch(e.target.value);
                  }}
                />
                <div className="pointer-events-none absolute inset-y-0 flex items-center px-5 text-gray-700">
                  <img src={process.env.PUBLIC_URL + "/icons/search.png"} alt="" />
                </div>
              </div>
            </div>
            {!loading ? (
              <Formik
                initialValues={{
                  drawings: drawings.map((d) => ({
                    ...d,
                    priority: d.priority || "",
                    designer_id: d.designer_id || "",
                    status: d.status || "",
                  })),
                }}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={handleBulkEditSubmit}
              >
                {({ isSubmitting, values, errors, touched, setFieldValue }) => (
                  <Form>
                    <FieldArray name="drawings">
                      {({ insert, remove, push }) => (
                        <div className="overflow-x-auto">
                          <div className="min-w-[1200px]">
                            <div className="sm:rounded-lg mt-5 w-full">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr className="bg-[#F8F8F8]">
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("opportunity_id")}
                                    >
                                      <span className="inline-flex items-center">
                                        Opportunity ID
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "opportunity_id"
                                              ? faSort
                                              : sortOrder === "asc"
                                              ? faSortUp
                                              : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("id")}
                                    >
                                      <span className="inline-flex items-center">
                                        Id
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={sortBy !== "id" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown}
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("customer_name")}
                                    >
                                      <span className="inline-flex items-center">
                                        Customer
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "customer_name" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("site_name")}
                                    >
                                      <span className="inline-flex items-center">
                                        Site
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={sortBy !== "site_name" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown}
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("date_requested")}
                                    >
                                      <span className="inline-flex items-center">
                                        Date Requested
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "date_requested" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("date_required")}
                                    >
                                      <span className="inline-flex items-center">
                                        Date Required
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "date_required" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("status_name")}
                                    >
                                      <span className="inline-flex items-center">
                                        Status
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "status_name" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("sales_person")}
                                    >
                                      <span className="inline-flex items-center">
                                        Sales Person
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "sales_person" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("priority_name")}
                                    >
                                      <span className="inline-flex items-center">
                                        Priority
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "priority_name" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th
                                      className="px-2 py-3 text-left font-[Inter] font-[600] text-[14px] text-[#6D6D6F] cursor-pointer"
                                      onClick={() => toggleSort("designer_name")}
                                    >
                                      <span className="inline-flex items-center">
                                        Designer
                                        <FontAwesomeIcon
                                          className="ml-2"
                                          icon={
                                            sortBy !== "designer_name" ? faSort : sortOrder === "asc" ? faSortUp : faSortDown
                                          }
                                        />
                                      </span>
                                    </th>
                                    <th className="px-2 py-3 text-center font-[Inter] font-[600] text-[14px] text-[#6D6D6F]">
                                      Options
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {values.drawings.length > 0 ? (
                                    values.drawings.map((drawing, index) => (
                                      <tr className="border border-gray-200 rounded-lg" key={index}>
                                        <td className="px-2 py-3 w-24">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.opportunity_id`}
                                            name={`drawings.${index}.opportunity_id`}
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.opportunity_id`} />
                                        </td>
                                        <td className="px-2 py-3 w-20">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3"
                                            id={`drawings.${index}.id`}
                                            name={`drawings.${index}.id`}
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.id`} />
                                        </td>
                                        <td className="px-2 py-3">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.customer_name`}
                                            name={`drawings.${index}.customer_name`}
                                            title={values.drawings[index].customer_name}
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.customer_name`} />
                                        </td>
                                        <td className="px-2 py-3">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.site_name`}
                                            name={`drawings.${index}.site_name`}
                                            title={values.drawings[index].site_name}
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.site_name`} />
                                        </td>
                                        <td className="px-2 py-3">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.date_requested`}
                                            name={`drawings.${index}.date_requested`}
                                            type="date"
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.date_requested`} />
                                        </td>
                                        <td className="px-2 py-3">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.date_required`}
                                            name={`drawings.${index}.date_required`}
                                            type="date"
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.date_required`} />
                                        </td>
                                        <td className={`px-2 py-3  ${
                                              getFieldColorClass(values.drawings[index].status, "Status") || "bg-white"
                                          }`}>
                                          <Field
                                            as="select"
                                            className={`w-full border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none`}
                                            id={`drawings.${index}.status`}
                                            name={`drawings.${index}.status`}
                                            onChange={(e) => {
                                              setFieldValue(`drawings.${index}.status`, e.target.value);
                                            }}
                                          >
                                            <option value={""}>Select Status</option>
                                            {statusOptions.map((option) => (
                                              <option key={option.id} value={option.id}>
                                                {option.field_code}
                                              </option>
                                            ))}
                                          </Field>
                                          <ErrorMessage name={`drawings.${index}.status`} />
                                        </td>
                                        <td className="px-2 py-3">
                                          <Field
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.sales_person`}
                                            name={`drawings.${index}.sales_person`}
                                            title={values.drawings[index].sales_person}
                                            disabled
                                          />
                                          <ErrorMessage name={`drawings.${index}.sales_person`} />
                                        </td>
                                        <td className={`px-2 py-3  ${
                                              getFieldColorClass(values.drawings[index].priority, "Priority") || "bg-white"
                                          }`}>
                                          <Field
                                            as="select"
                                            className={`w-full border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none`}
                                            id={`drawings.${index}.priority`}
                                            name={`drawings.${index}.priority`}
                                            onChange={(e) => {
                                              setFieldValue(`drawings.${index}.priority`, e.target.value);
                                            }}
                                          >
                                            <option value={""}>Select Priority</option>
                                            {priorityOptions.map((option) => (
                                              <option key={option.id} value={option.id}>
                                                {option.field_code}
                                              </option>
                                            ))}
                                          </Field>
                                          <ErrorMessage name={`drawings.${index}.priority`} />
                                        </td>
                                        <td className="px-2 py-3">
                                          <Field
                                            as="select"
                                            className="w-full bg-white border border-gray-200 text-gray-700 rounded py-2 px-3 focus:outline-none"
                                            id={`drawings.${index}.designer_id`}
                                            name={`drawings.${index}.designer_id`}
                                          >
                                            <option value={""}>Select Designer</option>
                                            {userData.map((user) => (
                                              <option key={user.id} value={user.id}>
                                                {user.firstname} {user.lastname}
                                              </option>
                                            ))}
                                          </Field>
                                          <ErrorMessage name={`drawings.${index}.designer_id`} />
                                        </td>
                                        <td className="px-2 py-3 flex gap-2">
                                          <Link to={`/opportunity/${drawing.opportunity_id}/drawing/${drawing.id}`}>
                                            <img
                                              src={process.env.PUBLIC_URL + "/icons/edit.png"}
                                              alt="edit"
                                              className="w-10 bg-teal-100 rounded-lg"
                                            />
                                          </Link>
                                        </td>
                                      </tr>
                                    ))
                                  ) : (
                                    <tr>
                                      <td colSpan="11" className="py-10">
                                        <div className="flex justify-center items-center w-full">
                                          <span className="text-center">No records found!</span>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </FieldArray>
                    {drawings.length ? (
                      <div className="hidden md:block font-Inter font-[300] text-nowrap mt-5">
                        <span>
                          <b>{drawings?.length}</b> Records found
                        </span>
                      </div>
                    ) : null}
                    {drawings.length ? (
                      <div className="flex justify-end mt-5">
                        <button
                          type="submit"
                          className={`py-2 px-8 text-white text-[18px] rounded-lg ${
                            isSubmitting ? "bg-gray-300" : "bg-black"
                          }`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <FontAwesomeIcon icon={faSpinner} spin />
                          ) : (
                            <span>
                              <FontAwesomeIcon icon={faCheck} className="mr-2" /> Save Changes
                            </span>
                          )}
                        </button>
                      </div>
                    ) : null}
                  </Form>
                )}
              </Formik>
            ) : (
              <div className="flex justify-center items-center">
                <FontAwesomeIcon icon={faSpinner} size="2xl" style={{ color: "#55A14A" }} spin />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex justify-between items-center mt-[20px] gap-[40px] md:p-5 bg-[#F8F8F8] rounded-l-[12px]">
        <div className="hidden md:block font-Inter font-[300] text-nowrap">
          <span>
            Showing <b>{page}</b> of <b>{Math.ceil(totalCount / limit)}</b> pages
          </span>
        </div>
        <div className="w-full flex justify-end">
          <ReactPaginate
            className="react_pagination_ul"
            breakLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md mr-4">
                <FontAwesomeIcon icon={faEllipsis} size="xl" />
              </span>
            }
            nextLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md">
                <FontAwesomeIcon icon={faAngleRight} size="xl" />
              </span>
            }
            onPageChange={handlePageClick}
            pageCount={Math.ceil(totalCount / limit)}
            previousLabel={
              <span className="w-10 h-10 flex justify-center items-center bg-white rounded-md mr-4">
                <FontAwesomeIcon icon={faAngleLeft} size="xl" />
              </span>
            }
            activeClassName="bg-black text-white"
            pageClassName="block text-black hover:bg-black hover:text-white rounded-md mr-4"
            pageLinkClassName="w-10 h-10 flex justify-center items-center"
            containerClassName="flex items-center justify-center"
          />
        </div>
      </div>
    </div>
  );
}

export default ListDrawings;